<template>
  <!-- Page Header -->
  <PageHeader 
    title="Gestion des paquets" 
    subtitle="Créez et gérez vos paquets de produits" 
    class="mb-0 pr-10" 
  />
  
  <div class="w-full px-2 sm:px-6 py-4">
    <!-- Bouton de retour conditionnel -->
    <div v-if="returnRoute" class="mb-4 -mt-6">
      <button
        @click="router.push(returnRoute)"
        class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-md hover:bg-blue-700 shadow-sm transition-colors"
      >
        <i class="fas fa-arrow-left mr-2"></i>
        Retour à la création de produit
      </button>
    </div>

    <!-- Formulaire d'ajout/modification -->
    <PackageForm 
      :packages-existants="allPackagesForTemplates"
      @submit="handleAddPackage" 
    />
    
    <!-- Tableau des paquets -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div class="px-6 py-4 border-b border-gray-200">
        <h3 class="text-lg font-medium text-black">Mes paquets</h3>
      </div>
      
      <PackageTable 
        :packages="packages"
        @edit="handleEditPackage"
        @delete="handleDeletePackage"
      />
    </div>
  </div>
  
  <!-- Modal de modification -->
  <PackageEditModal
    :is-visible="showEditModal"
    :pack="editingPack"
    @update:is-visible="showEditModal = $event"
    @submit="handleUpdatePackage"
  />

  <ModalComp
    :is-visible="showDeleteModal"
    title="Supprimer le paquet"
    size="sm"
    :hide-footer="true"
    @update:is-visible="closeDeleteModal"
  >
    <template #body>
      <p v-if="hasBoundProducts" class="mb-4 text-sm text-black">
        Tous les produits utilisant
        <strong>« {{ deletingPackage?.titre }} »</strong>
        seront associés au paquet sélectionné.
      </p>

      <p v-else class="mb-4 text-sm text-black">
        Ce paquet n'est associé à aucun produit. Il peut être supprimé sans
        remplacement.
      </p>

      <div v-if="hasBoundProducts && replacementPackages.length > 0">
        <label class="block mb-2 text-sm font-medium text-black">
          Paquet de remplacement
        </label>
        <select
          v-model.number="replacementPackageId"
          class="w-full px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="">Sélectionner un paquet</option>
          <option
            v-for="pkg in replacementPackages"
            :key="pkg.id"
            :value="pkg.id"
          >
            {{ pkg.titre }} ({{ pkg.quantites.join('-') }})
          </option>
        </select>
      </div>

      <p v-else-if="hasBoundProducts" class="text-sm text-red-600">
        Créez d'abord un autre paquet avec le même nombre de tailles.
      </p>

      <div class="flex justify-end gap-3 pt-5">
        <button
          type="button"
          class="px-4 py-2 text-sm border border-gray-300 rounded-md"
          @click="closeDeleteModal(false)"
        >
          Annuler
        </button>
        <button
          type="button"
          :disabled="isDeleting || (hasBoundProducts && !replacementPackageId)"
          class="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          @click="confirmDeletePackage"
        >
          {{ isDeleting ? 'Suppression...' : 'Supprimer' }}
        </button>
      </div>
    </template>
  </ModalComp>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { PageHeader, PackageForm, PackageTable, PackageEditModal, ModalComp } from '@/components'
import { getPacksByVendeur, getAllPacks, createPack, updatePack, packHasProducts, deletePack } from '@/api'
import { useAuthStore } from '@/stores/auth'
import { useNotification } from '@/composables/useNotification'
import type { Pack, PackageItem, PackageFormData, CreatePackInput } from '@/types/pack'
import {useRouter} from "vue-router";

const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()

// Déterminer la page de retour selon le paramètre 'from'
const fromPage = computed(() => route.query.from as string | undefined)
const returnRoute = computed(() => {
  if (fromPage.value === 'mel') return '/catalog/mel'
  return null
})
// Notifications
const { success: showSuccess, error: showError } = useNotification()

const packs = ref<Pack[]>([])
const allPacks = ref<Pack[]>([]) // Tous les paquets pour les templates
const isLoading = ref(false)

// État de la modal de modification
const showEditModal = ref(false)
const editingPack = ref<Pack | null>(null)
const showDeleteModal = ref(false)
const deletingPackage = ref<PackageItem | null>(null)
const replacementPackageId = ref<number | ''>('')
const isDeleting = ref(false)
const hasBoundProducts = ref(false)

const getPackLength = (pack: Pack) => {
  const quantites = [
    pack.p1, pack.p2, pack.p3, pack.p4, pack.p5, pack.p6,
    pack.p7, pack.p8, pack.p9, pack.p10, pack.p11, pack.p12
  ]
  return quantites.reduce(
    (lastIndex, quantite, index) => Number(quantite) > 0 ? index + 1 : lastIndex,
    0
  )
}

// Transformer tous les packs pour les templates
const allPackagesForTemplates = computed(() => {
  return allPacks.value.map(pack => {
    const toutesQuantites = [
      pack.p1, pack.p2, pack.p3, pack.p4, pack.p5, pack.p6,
      pack.p7, pack.p8, pack.p9, pack.p10, pack.p11, pack.p12
    ]
    
    const nombreTailles = getPackLength(pack)
    const quantites = toutesQuantites.slice(0, nombreTailles)
    
    return {
      id: pack.id_pack,
      titre: pack.titre || 'Sans titre',
      quantites,
      nombreTailles,
      dateCreation: new Date().toISOString().split('T')[0]
    }
  }).filter(pack =>
    pack.nombreTailles > 0 && pack.quantites.every(quantite => quantite > 0)
  )
})

// Transformer les packs pour le composant PackageTable avec groupement
const packages = computed(() => {
  const transformedPacks = packs.value
    .map(pack => {
      const toutesQuantites = [
        pack.p1, pack.p2, pack.p3, pack.p4, pack.p5, pack.p6,
        pack.p7, pack.p8, pack.p9, pack.p10, pack.p11, pack.p12
      ]
      
      const nombreTailles = getPackLength(pack)
      const quantites = toutesQuantites.slice(0, nombreTailles)
      
      return {
        id: pack.id_pack,
        titre: pack.titre || 'Sans titre',
        quantites,
        nombreTailles,
        dateCreation: new Date().toISOString().split('T')[0]
      }
    })
    .sort((a, b) => a.nombreTailles - b.nombreTailles) // Tri par ordre croissant du nombre de tailles
  
  // Grouper par nombre de tailles
  const grouped = transformedPacks.reduce((groups, pack) => {
    const key = pack.nombreTailles
    if (!groups[key]) {
      groups[key] = []
    }
    groups[key].push(pack)
    return groups
  }, {} as Record<number, typeof transformedPacks>)
  
  // Retourner les groupes triés par nombre de tailles
  return Object.keys(grouped)
    .map(Number)
    .sort((a, b) => a - b)
    .flatMap(nombreTailles => grouped[nombreTailles])
})

const replacementPackages = computed(() => {
  if (!deletingPackage.value) return []
  return packages.value.filter(
    pack =>
      pack.id !== deletingPackage.value?.id &&
      pack.nombreTailles === deletingPackage.value?.nombreTailles
  )
})

// Méthodes de gestion des données
const loadPackages = async () => {
  if (!authStore.user?.id_vendeur) {
    return
  }

  isLoading.value = true
  
  try {
    // Charger les paquets du vendeur connecté
    packs.value = await getPacksByVendeur(authStore.user.id_vendeur)
    
    // Charger TOUS les paquets pour les templates
    allPacks.value = await getAllPacks()
  } catch (err) {
    showError('Erreur lors du chargement des paquets')
  } finally {
    isLoading.value = false
  }
}

const handleAddPackage = async (formData: PackageFormData) => {
  if (!authStore.user?.id_vendeur) {
    return
  }

  try {
    // Créer l'objet avec les quantités directement depuis formData
    const input: CreatePackInput = {
      id_vendeur: authStore.user.id_vendeur,
      titre: formData.titre || null,
      p1: formData.p1 || 0,
      p2: formData.p2 || 0,
      p3: formData.p3 || 0,
      p4: formData.p4 || 0,
      p5: formData.p5 || 0,
      p6: formData.p6 || 0,
      p7: formData.p7 || 0,
      p8: formData.p8 || 0,
      p9: formData.p9 || 0,
      p10: formData.p10 || 0,
      p11: formData.p11 || 0,
      p12: formData.p12 || 0,
    }
    
    const newPack = await createPack(input)
    packs.value = [newPack, ...packs.value]
    showSuccess(`Paquet "${newPack.titre}" créé avec succès !`)
  } catch (err) {
    showError(err instanceof Error ? err.message : 'Erreur lors de la création du paquet')
  }
}

const handleEditPackage = (packageId: number) => {
  const pack = packs.value.find(p => p.id_pack === packageId)
  if (pack) {
    editingPack.value = { ...pack }
    showEditModal.value = true
  }
}

const handleUpdatePackage = async (updatedPack: Pack) => {
  try {
    const result = await updatePack({
      id_pack: updatedPack.id_pack,
      titre: updatedPack.titre || undefined,
      p1: updatedPack.p1,
      p2: updatedPack.p2,
      p3: updatedPack.p3,
      p4: updatedPack.p4,
      p5: updatedPack.p5,
      p6: updatedPack.p6,
      p7: updatedPack.p7,
      p8: updatedPack.p8,
      p9: updatedPack.p9,
      p10: updatedPack.p10,
      p11: updatedPack.p11,
      p12: updatedPack.p12,
    })
    
    const index = packs.value.findIndex(p => p.id_pack === result.id_pack)
    if (index !== -1) {
      packs.value[index] = result
    }
    
    showEditModal.value = false
    showSuccess(`Paquet "${result.titre || 'Sans titre'}" modifié avec succès !`)
  } catch (err) {
    showError('Erreur lors de la modification du paquet')
  }
}

const handleDeletePackage = async (packageId: number) => {
  deletingPackage.value = packages.value.find(pack => pack.id === packageId) || null
  if (!deletingPackage.value) return

  try {
    hasBoundProducts.value = await packHasProducts(deletingPackage.value.id)
    replacementPackageId.value = ''
    showDeleteModal.value = true
  } catch (err) {
    deletingPackage.value = null
    showError(err instanceof Error ? err.message : 'Erreur lors de la vérification du paquet')
  }
}

const closeDeleteModal = (visible: boolean) => {
  if (isDeleting.value) return
  showDeleteModal.value = visible
  if (!visible) {
    deletingPackage.value = null
    replacementPackageId.value = ''
  }
}

const confirmDeletePackage = async () => {
  if (
    !deletingPackage.value ||
    (hasBoundProducts.value && !replacementPackageId.value) ||
    isDeleting.value
  ) return

  isDeleting.value = true
  try {
    await deletePack(
      deletingPackage.value.id,
      hasBoundProducts.value ? Number(replacementPackageId.value) : null,
    )
    const deletedTitle = deletingPackage.value.titre
    showDeleteModal.value = false
    deletingPackage.value = null
    replacementPackageId.value = ''
    await loadPackages()
    showSuccess(`Paquet "${deletedTitle}" supprimé avec succès !`)
  } catch (err) {
    showError(err instanceof Error ? err.message : 'Erreur lors de la suppression du paquet')
  } finally {
    isDeleting.value = false
  }
}

onMounted(async () => {


  if (!authStore.isInitialized) {
    await authStore.checkAuth()
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: 'auth' })
    return
  }
  await loadPackages()
})
</script>

<style lang="scss" scoped></style>
